import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { LinkDirective } from '../../../_shared/directives/link.directive';
import { ICardIcon } from '../../../_shared/models/card/card-icon.model';
import { ICardComponent } from '../../../_shared/models/card/card.model';
import { ILink, EMPTY_LINK } from '../../../_shared/models/link.model';
import { LinkService } from '../../../_shared/services/link.service';
import { SnowplowService } from '../../../_shared/services/snowplow.service';
import { CardContainer } from '../../../_shared/types/card/card-container.type';
import { CardCatalogueDataSource } from '../../../_shared/types/card-grid-data-source.type';
import { withConfigFallback } from '../../../_shared/utils/with-config-fallback';
import { IconComponent } from '../../../icon/icon.component';
import { IconName } from '../../../icon/icons';

@Component({
    selector: 'sirius-card-icon',
    templateUrl: './card-icon.component.html',
    styleUrls: ['./card-icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [LinkDirective, IconComponent],
    host: {
        // Keeps the card light on dark sections.
        'class': 'is-light',
    },
})
export class CardIconComponent implements ICardComponent<ICardIcon> {
    private _snowplowService = inject(SnowplowService);
    private _linkService = inject(LinkService);

    readonly config = input<ICardIcon | undefined>(undefined);
    readonly dataSource = input<CardCatalogueDataSource>('other');
    readonly container = input<CardContainer>('slider');

    // Public inputs keep their natural API name. Consumers bind `[icon]`, `[title]`, `[link]`.
    readonly icon = input<IconName>('palletizing_packaging_pictogram');
    readonly title = input('');
    readonly link = input<ILink>(EMPTY_LINK);

    // `ICardIcon.icon` is `{ icon: IconName }`; the standalone input is the
    // flat `IconName`. Unwrap the nested shape so the template reads a single value.
    protected readonly resolvedIcon = computed<IconName>(() => this.config()?.icon.icon ?? this.icon());
    protected readonly resolvedTitle = withConfigFallback<ICardIcon, 'title', string>(this.config, 'title', this.title);
    protected readonly resolvedLink = withConfigFallback<ICardIcon, 'link', ILink>(this.config, 'link', this.link);

    readonly selectEvent = output<void>();

    onClick(): void {
        this.trackLinkClick();
        this.selectEvent.emit();
    }

    private trackLinkClick(): void {
        const link = this.resolvedLink();

        this._snowplowService.track('linkClick', {
            category: 'card',
            container: this.container(),
            identifier: link.tracking?.parameters['identifier'],
            target_url: this._linkService.getFullPath(link.href),
            outbound: link.target === '_blank',
            element_content: link.title,
            context: [],
        });
    }
}
